import React, { useState } from "react";

interface AsyncButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onClick"> {
  onClick: () => Promise<any>;
  loadingText?: string;
}

/**
 * Drop-in replacement for <button> with async onClick.
 * Automatically disables and shows a loading indicator while the promise is pending.
 * Re-enables after resolve or reject so the user can retry on error.
 */
export function AsyncButton({
  onClick,
  children,
  loadingText,
  disabled,
  style,
  ...props
}: AsyncButtonProps) {
  const [loading, setLoading] = useState(false);

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (loading || disabled) return;
    setLoading(true);
    try {
      await onClick();
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      {...props}
      onClick={handleClick}
      disabled={disabled || loading}
      style={{
        ...style,
        opacity: loading ? 0.6 : disabled ? 0.5 : 1,
        cursor: loading || disabled ? "not-allowed" : "pointer",
        transition: "opacity 0.15s",
      }}
    >
      {loading ? (loadingText ?? `${children}…`) : children}
    </button>
  );
}
